// =========================================================================
// Cost summary projections. Fork-owned (zone C).
//
// The demo's cost block is one 40-line IIFE that derives eight tables inline
// and hands four of them to `barChart()`. This file is that IIFE, split into
// named functions with the demo's arithmetic kept exactly — the same
// categories in the same order, the same two different divisors for cost per
// operating hour (the engine keeps both because both appear on screen), the
// same `<= '2026-08'` month ceiling.
//
// Where the demo wrote a figure straight into the markup, this file returns a
// value and a **source payload** beside it. That is the one structural change:
// the demo's promise is that every figure opens its source, and on the cost
// page the source is the long-format ledger rows, which nothing on screen can
// show without the drawer.
//
// Transformation layer (layer 3) — pure, no React, no clock, no fetch.
// @see docs/logic/costs/summary.md
// =========================================================================

import {
  BY_ID,
  COST_PER_MACHINE,
  ENERGY_FACTORS,
  FLEET,
  MINES,
  MINE_ORDER,
  RATES,
} from '../../data/core';
import {
  CLSNAME,
  CLS_ORDER,
  correctiveSharePct,
  costCategoryTotals,
  costToServeBySite,
  fmt$,
  fmtk,
  groupNum,
  highestSiteCostPerOpHour,
  meanCostPerOpHour,
  MSHORT,
  topCostMachines,
  type CostToServeRow,
  type MachineCostCategoryCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import { COST_CATEGORIES, type CostCategoryName } from '../../shared/viz/tokens';
import type { StackedRow } from '../../shared/viz/StackedBar';
import type { BarRow } from '../../shared/viz/BarChart';
import type { MineCode, MachineClass } from '../../schema/machine';
import type { CostByCrewCategory, CostByMonthMine } from '../../schema/cost';
import type { MachineEnergy } from '../../schema/telemetry';

/** A source-drawer table payload, as `SourceDrawerBody` reads it. */
export interface SourceTable {
  kind: 'table';
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
  provenance: string;
}

/**
 * The demo's month ceiling (`.filter(m => m <= '2026-08')`).
 *
 * `cost_by_month_mine` runs to `2026-10` because the ledger carries two
 * months of accrual beyond the snapshot. Charting them would draw two
 * collapsing bars at the right-hand edge that look like a crash and are
 * nothing of the kind, which is why the demo cut them and why this does too.
 */
const LAST_CLOSED_MONTH = '2026-08';

// ---------------------------------------------------------------------------
// Headline figures
// ---------------------------------------------------------------------------

/** The figures across the top of the page. */
export interface CostHeadline {
  /** 18-month ledger across every site, AUD. */
  readonly ledger: number;
  /** The lost-production share of it, AUD. */
  readonly lostProduction: number;
  /** Lost production as a percentage of the ledger. */
  readonly lostPct: number;
  readonly meanPerOpHour: number;
  readonly highestSitePerOpHour: number;
  readonly correctivePct: number;
  readonly sites: readonly CostToServeRow[];
}

/**
 * The headline figures.
 *
 * `ledger` sums {@link costByMineRows}, not {@link costToServeBySite}: the two
 * differ, and deliberately. `cost_by_month_mine` is the monthly ledger
 * including lost production; `cost_per_machine` is the per-machine roll-up of
 * direct cost. The demo prints the first on this page's headline and the
 * second in its cost-to-serve table, and both are correct for what they say
 * they are. Conflating them into one number would be the easy mistake here.
 */
export function costHeadline(
  ctx: WorkOrderCtx,
  byMonthMine: readonly CostByMonthMine[],
): CostHeadline {
  const rows = costByMineRows(byMonthMine);
  const ledger = rows.reduce((a, r) => a + r.total, 0);
  const lost = rows.reduce((a, r) => a + (r.values['Lost production'] ?? 0), 0);
  return {
    ledger,
    lostProduction: lost,
    lostPct: ledger > 0 ? Math.round((lost / ledger) * 100) : 0,
    meanPerOpHour: meanCostPerOpHour(),
    highestSitePerOpHour: highestSiteCostPerOpHour(),
    correctivePct: correctiveSharePct(ctx),
    sites: costToServeBySite(ctx),
  };
}

/** One per-site headline tile. */
export interface SiteCostTile {
  readonly mine: MineCode;
  readonly name: string;
  readonly machines: number;
  readonly total: number;
  readonly lostPct: number;
  readonly labourRate: number;
  readonly operatorModel: string;
  readonly dataMode: string;
}

/** The six per-site tiles under the total. Demo line 2391. */
export function siteCostTiles(byMonthMine: readonly CostByMonthMine[]): SiteCostTile[] {
  const rows = costByMineRows(byMonthMine);
  return MINE_ORDER.map((mine, i) => {
    const row = rows[i];
    const lost = row.values['Lost production'] ?? 0;
    return {
      mine,
      name: MINES[mine].name,
      machines: FLEET.filter((f) => f.mine === mine).length,
      total: row.total,
      lostPct: row.total > 0 ? Math.round((lost / row.total) * 100) : 0,
      labourRate: MINES[mine].labour_rate,
      // The demo drops the parenthetical contractor name on this line.
      operatorModel: MINES[mine].operator_model.split(' (')[0],
      dataMode: MINES[mine].data_mode,
    };
  });
}

// ---------------------------------------------------------------------------
// Stacked bars
// ---------------------------------------------------------------------------

/** Sum one slice of the long-format ledger into a category map. */
function tally<T extends { category: string; amount_aud: number }>(
  rows: readonly T[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const category of COST_CATEGORIES) out[category] = 0;
  for (const row of rows) {
    out[row.category] = (out[row.category] ?? 0) + row.amount_aud;
  }
  return out;
}

/** Total of a category map. */
const sum = (values: Record<string, number>): number =>
  Object.values(values).reduce((a, b) => a + b, 0);

/** A site's stacked row, with its total kept alongside for the tiles. */
export type MineCostRow = StackedRow & { readonly total: number };

/** Cost by mine and category, in `MINE_ORDER`. Demo `mineRows`. */
export function costByMineRows(byMonthMine: readonly CostByMonthMine[]): MineCostRow[] {
  return MINE_ORDER.map((mine) => {
    const values = tally(byMonthMine.filter((r) => r.mine === mine));
    return { label: MINES[mine].name, values, total: sum(values) };
  });
}

/** Cost by crew and category, largest first. Demo `crewRows`. */
export function costByCrewRows(byCrewCat: readonly CostByCrewCategory[]): StackedRow[] {
  const byCrew = new Map<string, { mine: MineCode; rows: CostByCrewCategory[] }>();
  for (const row of byCrewCat) {
    const entry = byCrew.get(row.crew) ?? { mine: row.mine, rows: [] };
    entry.rows.push(row);
    byCrew.set(row.crew, entry);
  }
  return [...byCrew.entries()]
    .map(([crew, entry]) => {
      const values = tally(entry.rows);
      return { label: `${crew} (${entry.mine})`, values, total: sum(values) };
    })
    .sort((a, b) => b.total - a.total)
    .map(({ label, values }) => ({ label, values }));
}

/**
 * Monthly ledger, one row per month, stacked by site. Demo `ch-cost3`.
 *
 * Stacked by site rather than by category because the question this chart
 * answers is "which site moved", and a category stack cannot answer it.
 */
export function costByMonthRows(byMonthMine: readonly CostByMonthMine[]): {
  rows: StackedRow[];
  categories: string[];
} {
  const months = [...new Set(byMonthMine.map((r) => r.month))]
    .filter((m) => m <= LAST_CLOSED_MONTH)
    .sort();
  const categories = MINE_ORDER.map((m) => MINES[m].name);

  return {
    categories,
    rows: months.map((month) => {
      const values: Record<string, number> = {};
      for (const mine of MINE_ORDER) {
        values[MINES[mine].name] = byMonthMine
          .filter((r) => r.month === month && r.mine === mine)
          .reduce((a, r) => a + r.amount_aud, 0);
      }
      return { label: monthLabel(month), values };
    }),
  };
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** `'2026-08'` → `'Aug 26'`. Never reads the clock. */
function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  return `${MONTH_NAMES[Number(m) - 1]} ${year.slice(2)}`;
}

/** Category totals across the whole base, largest first. Demo line 3663. */
export function categoryBars(ctx: MachineCostCategoryCtx): BarRow[] {
  const totals = costCategoryTotals(ctx);
  return totals.map((t) => ({
    label: t.category,
    value: t.amount,
    valueLabel: fmtk(t.amount),
  }));
}

// ---------------------------------------------------------------------------
// Cost per operating hour by class
// ---------------------------------------------------------------------------

/** One row of the class matrix: a site and its mean A$/h per machine class. */
export interface ClassCostRow {
  readonly mine: MineCode;
  readonly name: string;
  /** Mean A$ per operating hour by class, or `null` where the site has none. */
  readonly byClass: Readonly<Record<string, number | null>>;
}

/**
 * Mean cost per operating hour, by site and machine class. Demo `perCls`.
 *
 * The demo's own reading of this table, kept because it is the argument the
 * page exists to make: Kalbarri is highest in every underground class despite
 * the shallowest ground and the cheapest labour, because its reactive work
 * averages 9 h down against 5 h elsewhere.
 */
export function classCostMatrix(): {
  rows: ClassCostRow[];
  classes: readonly MachineClass[];
  /** Worst site per class, for the highlight. */
  worstByClass: Readonly<Record<string, MineCode | null>>;
} {
  const rows: ClassCostRow[] = MINE_ORDER.map((mine) => {
    const byClass: Record<string, number | null> = {};
    for (const cls of CLS_ORDER) {
      const costs = COST_PER_MACHINE.filter(
        (c) => BY_ID[c.machine_id].mine === mine && BY_ID[c.machine_id].cls === cls,
      );
      byClass[cls] = costs.length
        ? costs.reduce((a, c) => a + c.cost_per_op_hour_aud, 0) / costs.length
        : null;
    }
    return { mine, name: MINES[mine].name, byClass };
  });

  const worstByClass: Record<string, MineCode | null> = {};
  for (const cls of CLS_ORDER) {
    let worst: { mine: MineCode; value: number } | null = null;
    for (const row of rows) {
      const value = row.byClass[cls];
      if (value == null) continue;
      if (!worst || value > worst.value) worst = { mine: row.mine, value };
    }
    worstByClass[cls] = worst?.mine ?? null;
  }

  return { rows, classes: CLS_ORDER, worstByClass };
}

/** Class display name — the engine's `CLSNAME`. */
export const className_ = (cls: MachineClass): string => CLSNAME[cls];

/** The ten most expensive machines per operating hour. Demo line 3664. */
export function topMachineRows(): {
  machineId: string;
  site: string;
  model: string;
  perOpHour: number;
  total: number;
}[] {
  return topCostMachines().map((c) => ({
    machineId: c.machine_id,
    site: MINES[BY_ID[c.machine_id].mine].name,
    model: BY_ID[c.machine_id].model,
    perOpHour: c.cost_per_op_hour_aud,
    total: Math.round(c.amount_aud),
  }));
}

// ---------------------------------------------------------------------------
// Rate card
// ---------------------------------------------------------------------------

/** One site's row of the rate card. */
export interface RateCardRow {
  readonly mine: MineCode;
  readonly name: string;
  readonly labour: number;
  readonly dc: string;
  readonly freightDays: number;
  /** Lost production A$/h by machine class; `null` where the site prices none. */
  readonly lost: Readonly<Record<string, number | null>>;
}

/**
 * The rate card — what an hour of labour and an hour of lost production cost
 * at each site.
 *
 * This is the table that makes every other figure on the page defensible.
 * Lost production is the largest single ledger category and it is *priced*,
 * not estimated: `A$2,200` for an hour of a truck at Mount Isolde is a number
 * from the customer's own contract, and this is where a sceptical reader goes
 * to check it.
 */
export function rateCard(): { rows: RateCardRow[]; classes: readonly MachineClass[] } {
  return {
    classes: CLS_ORDER,
    rows: MINE_ORDER.map((mine) => {
      const rates = RATES[mine];
      const lost: Record<string, number | null> = {};
      for (const cls of CLS_ORDER) lost[cls] = rates.lost[cls] ?? null;
      return {
        mine,
        name: MINES[mine].name,
        labour: rates.labour,
        dc: rates.dc,
        freightDays: rates.freight_days,
        lost,
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// Energy and emissions
// ---------------------------------------------------------------------------

/** One site's energy and emissions over the 12-day window. */
export interface EnergyRow {
  readonly mine: MineCode;
  readonly name: string;
  readonly diesel: number;
  readonly kwh: number;
  /** Tonnes CO₂-e. */
  readonly co2t: number;
  /** kg CO₂-e per operating hour. */
  readonly perHour: number;
  readonly powerSource: string;
  readonly gridFactor: number;
}

/** Energy and emissions by site. Demo `eMine`. */
export function energyBySite(energy: readonly MachineEnergy[]): EnergyRow[] {
  return MINE_ORDER.map((mine) => {
    const rows = energy.filter((e) => e.mine === mine);
    const hours = rows.reduce((a, e) => a + e.hours, 0);
    const co2 = rows.reduce((a, e) => a + e.co2_kg, 0);
    const factors = ENERGY_FACTORS.find((f) => f.mine === mine);
    return {
      mine,
      name: MINES[mine].name,
      diesel: rows.reduce((a, e) => a + e.diesel_l, 0),
      kwh: rows.reduce((a, e) => a + e.energy_kwh, 0),
      co2t: co2 / 1000,
      perHour: hours > 0 ? co2 / hours : 0,
      powerSource: factors?.power_source ?? '—',
      gridFactor: factors?.grid_kgco2_per_kwh ?? 0,
    };
  });
}

/** Emissions intensity by drive type, mean over the machines. Demo `eDrive`. */
export function intensityByDrive(energy: readonly MachineEnergy[]): BarRow[] {
  const byDrive = new Map<string, number[]>();
  for (const e of energy) {
    const list = byDrive.get(e.drive) ?? [];
    list.push(e.co2_kg_per_hour);
    byDrive.set(e.drive, list);
  }
  return [...byDrive.entries()]
    .map(([drive, values]) => ({
      label: drive,
      value: values.reduce((a, b) => a + b, 0) / values.length,
      detail: `${values.length} machine${values.length === 1 ? '' : 's'}`,
    }))
    .sort((a, b) => b.value - a.value)
    .map((row) => ({ ...row, valueLabel: `${row.value.toFixed(1)} kg/h` }));
}

/**
 * Demo line 2404. The short class noun the emissions chart pairs with the
 * site name (`Broken Ridge · loader`). Local to that one chart: the demo
 * writes the map inline there and nowhere else.
 */
export const EMISSIONS_CLASS_SHORT: Readonly<Record<MachineClass, string>> = {
  jumbo: 'jumbo',
  longhole: 'prod. rig',
  lhd: 'loader',
  truck: 'truck',
  sdrill: 'surface drill',
};

/**
 * Emissions intensity by machine class and mine. Demo `rowsE`, line 2404.
 *
 * One bar per (mine, class) pair that has energy rows, in `MINE_ORDER` then
 * `CLS_ORDER` — the demo does not sort, so neither does this: the chart reads
 * as the sites in their usual order, each with its classes. Each value is the
 * **mean of the machines' `co2_kg_per_hour`** (not total CO₂ over total
 * hours), exactly as the demo computes it, so it sits on the same basis as the
 * drive-type chart beside it. A machine's class comes from `BY_ID`; an energy
 * row for a machine missing from the fleet is skipped.
 *
 * **Deviation — the label's site half.** The demo labels a bar with the first
 * word of the site name (`MINES[m].name.split(' ')[0]`), which prints
 * `Mount · jumbo` twice — once for Mount Isolde, once for Mount Read. The port
 * uses the demo's own short site names (`MSHORT`, demo line 1848), so the two
 * read `Mt Isolde · jumbo` and `Mt Read · jumbo`. Values and order unchanged.
 */
export function intensityByClassAndMine(energy: readonly MachineEnergy[]): BarRow[] {
  const rows: BarRow[] = [];
  for (const mine of MINE_ORDER) {
    for (const cls of CLS_ORDER) {
      const xs = energy.filter((e) => e.mine === mine && BY_ID[e.machine_id]?.cls === cls);
      if (xs.length === 0) continue;
      const value = xs.reduce((a, e) => a + e.co2_kg_per_hour, 0) / xs.length;
      rows.push({
        label: `${MSHORT[mine]} · ${EMISSIONS_CLASS_SHORT[cls]}`,
        value,
        valueLabel: `${value.toFixed(1)} kg/h`,
      });
    }
  }
  return rows;
}

/** The diesel factor, identical at every site. */
export const DIESEL_FACTOR = ENERGY_FACTORS[0]?.diesel_kgco2_per_l ?? 2.68;

// ---------------------------------------------------------------------------
// Source payloads
// ---------------------------------------------------------------------------

/** Long-format ledger rows behind a mine's total, or the whole ledger. */
export function ledgerSource(
  byMonthMine: readonly CostByMonthMine[],
  mine?: MineCode,
): SourceTable {
  const rows = byMonthMine.filter((r) => (mine ? r.mine === mine : true));
  return {
    kind: 'table',
    title: mine ? `${MINES[mine].name} · monthly ledger` : 'Monthly ledger by site and category',
    columns: ['Site', 'Month', 'Category', 'Amount A$'],
    rows: rows.map((r) => [MINES[r.mine].name, r.month, r.category, Math.round(r.amount_aud)]),
    provenance: `SAP PM + ledger · cost_by_month_mine · ${groupNum(rows.length)} of ${groupNum(byMonthMine.length)} rows · 21 months to Oct 2026`,
  };
}

/** The per-machine cost roll-up behind the cost-to-serve table. */
export function costToServeSource(ctx: WorkOrderCtx): SourceTable {
  const sites = costToServeBySite(ctx);
  return {
    kind: 'table',
    title: 'Cost to serve by site',
    columns: ['Site', 'Machines', 'Ledger A$', 'A$ / op h', 'Labour A$/h', 'Corrective %'],
    rows: sites.map((s) => [
      MINES[s.mine].name,
      s.machines,
      Math.round(s.total),
      s.costPerOpHour,
      s.labourRate,
      s.correctivePct,
    ]),
    provenance: `core.json cost_per_machine · ${COST_PER_MACHINE.length} machines · 18-month window · divided by cost rows, not machine count`,
  };
}

/** The per-machine rows behind the class matrix. */
export function perMachineSource(): SourceTable {
  return {
    kind: 'table',
    title: 'Cost per machine · 18 months',
    columns: ['Machine', 'Site', 'Model', 'Class', 'A$ all-in', 'Op hours', 'A$ / op h'],
    rows: COST_PER_MACHINE.slice()
      .sort((a, b) => b.cost_per_op_hour_aud - a.cost_per_op_hour_aud)
      .map((c) => [
        c.machine_id,
        MINES[BY_ID[c.machine_id].mine].name,
        BY_ID[c.machine_id].model,
        CLSNAME[BY_ID[c.machine_id].cls],
        Math.round(c.amount_aud),
        c.op_hours_18m,
        c.cost_per_op_hour_aud,
      ]),
    provenance: `core.json cost_per_machine · ${COST_PER_MACHINE.length} rows, worst first · all-in ÷ operating hours`,
  };
}

/** The rate card rows, as the ledger's own pricing basis. */
export function rateCardSource(): SourceTable {
  const { rows, classes } = rateCard();
  return {
    kind: 'table',
    title: 'Rate card · labour and lost production',
    columns: ['Site', 'Labour A$/h', 'DC', 'Freight days', ...classes.map((c) => `${CLSNAME[c]} A$/h`)],
    rows: rows.map((r) => [
      r.name,
      r.labour,
      r.dc,
      r.freightDays,
      ...classes.map((c) => r.lost[c]),
    ]),
    provenance: 'core.json rates · one row per site · contracted labour rate and priced lost production by machine class',
  };
}

/** The per-machine energy rows behind the emissions figures. */
export function energySource(energy: readonly MachineEnergy[]): SourceTable {
  return {
    kind: 'table',
    title: 'Energy and emissions by machine · 12 days',
    columns: ['Machine', 'Site', 'Drive', 'Hours', 'Diesel L', 'kWh', 'kg CO₂-e', 'kg/op h'],
    rows: energy.map((e) => [
      e.machine_id,
      MINES[e.mine].name,
      e.drive,
      e.hours,
      Math.round(e.diesel_l),
      Math.round(e.energy_kwh),
      Math.round(e.co2_kg),
      e.co2_kg_per_hour,
    ]),
    provenance: `telemetry.json energy · ${energy.length} machines · diesel ${DIESEL_FACTOR} kg/L plus site grid factors`,
  };
}

/** The category totals behind the "where the cost sits" bars. */
export function categorySource(ctx: MachineCostCategoryCtx): SourceTable {
  const totals = costCategoryTotals(ctx);
  const grand = totals.reduce((a, t) => a + t.amount, 0);
  return {
    kind: 'table',
    title: 'Where the cost sits · by category',
    columns: ['Category', 'Amount A$', 'Share %'],
    rows: totals.map((t) => [
      t.category,
      Math.round(t.amount),
      grand > 0 ? Math.round((t.amount / grand) * 100) : 0,
    ]),
    provenance: `costs.json cost_by_machine_cat · ${groupNum(ctx.costByMachineCat.length)} rows summed by category`,
  };
}

/** Formatters re-exported so the scene has one import for money. */
export { fmt$, fmtk, groupNum };
export type { CostCategoryName };
